import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6fd0f044"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex flex-col gap-6" }

import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber } from '@/services/utils';
import { dateUtils } from '@/services/dateUtils';
import { useMatomo } from '@/services/matomo/matomo.service';
import IbxScopedCss from '@/components/IbxScopedCss/IbxScopedCss.vue';
import { convertBaseTimeToSSMMHH } from '@/views/dashboard/dashboard.charts.utils';
import { LOG_LEVEL_WORKLOADS } from '@/views/workloads/workloads.const';
import WorkloadViewSummaryBlock from './workload-view-summary-block.vue';
import WorkloadViewSummaryBlockEntries from './workload-view-summary-block-entries.vue';
import WorkloadViewSummaryErrors from './workload-view-summary-errors.vue';
import WorkloadViewSummaryConfigurations from './workload-view-summary-configurations.vue';
import { useWorkloadViewSummaryDataProvider } from './workload-view-summary.helpers';

type Props = {
  id: string;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'workload-view-summary',
  props: {
    id: null
  },
  setup(__props: any) {

const props = __props as {
  id: string;
}

const { t } = useI18n();


const { summary } = useWorkloadViewSummaryDataProvider(props.id);

const matomo = useMatomo();

onMounted(() => {
  matomo?.trackEvent({
    category: 'Build page',
    action: 'Click',
    name: 'Build summary tab',
  });
});

const detailsEntries = computed(() => [
  {
    title: t('workloads.details.summaryTab.details.workingDirectory'),
    value: summary?.value?.workingDir,
    dataTestId: 'workload-view-summary-block-entries--working-dir',
  },
  { title: t('workloads.details.summaryTab.details.target'), value: summary?.value?.buildTarget, dataTestId: 'workload-view-summary-block-entries--build-target' },

  { title: t('workloads.details.summaryTab.details.version'), value: summary?.value?.ibVersion, dataTestId: 'workload-view-summary-block-entries--ib-version' },
  {
    title: t('workloads.details.summaryTab.details.totalTasks'),
    value: formatNumber(summary?.value?.totalTasks || 0),
    dataTestId: 'workload-view-summary-block-entries--total-tasks',
    dataOriginalValue: `${summary?.value?.totalTasks}`,
  },
]);

const timesEntries = computed(() => [
  {
    title: t('workloads.details.summaryTab.time.duration'),
    value: convertBaseTimeToSSMMHH(summary?.value?.duration || 0),
    dataTestId: 'workload-view-summary-block-entries--duration',
    dataOriginalValue: summary?.value?.duration,
  },
  {
    title: t('workloads.details.summaryTab.time.startTime'),
    value: dateUtils.getRunningSinceDateFormat(summary.value?.startTime),
    dataTestId: 'workload-view-summary-block-entries--start-time',
    dataOriginalValue: summary.value?.startTime,
  },
  {
    title: t('workloads.details.summaryTab.time.endTime'),
    value: dateUtils.getRunningSinceDateFormat(summary.value?.endTime),
    dataTestId: 'workload-view-summary-block-entries--end-time',
    dataOriginalValue: summary.value?.endTime,
  },
]);

const initiatorEntries = computed(() => [
  {
    title: t('workloads.details.summaryTab.initiator.buildGroup'),
    value: summary.value?.buildGroup,
    dataTestId: 'workload-view-summary-block-entries--build-group',
  },
  { title: t('workloads.details.summaryTab.initiator.agent'), value: summary.value?.initiatorDescription, dataTestId: 'workload-view-summary-block-entries--initiator-agent' },
  {
    title: t('workloads.details.summaryTab.initiator.logLevel'),
    // @ts-ignore
    value: LOG_LEVEL_WORKLOADS[summary.value?.initiatorLogLevel || 0],
    dataTestId: 'workload-view-summary-block-entries--initiator-log-level',
    dataOriginalValue: `${summary.value?.initiatorLogLevel}`,
  },
]);

const errors = computed(() => ({
  failureReason: summary.value?.failReason || '',
  buildErrors: summary.value?.errorsNumber || 0,
  buildWarnings: summary.value?.warningsNumber || 0,
  ibErrors: summary.value?.sysErrorsNumber || 0,
  ibWarnings: summary.value?.sysWarningsNumber || 0,
}));

const configurations = computed(() => ({
  cache: !!summary.value?.isBuildCache,
  cloud: !!summary.value?.isCloudHelpers,
  predictive: !!summary.value?.isPredictiveExecution,
}));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(IbxScopedCss, { class: "workload-view-summary bg-System/app_background flex flex-row px-[24px] w-full justify-center" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(WorkloadViewSummaryBlock, {
          title: _ctx.$t('workloads.details.summaryTab.details.title'),
          icon: "uis uis-info-circle"
        }, {
          default: _withCtx(() => [
            _createVNode(WorkloadViewSummaryBlockEntries, { entries: _unref(detailsEntries) }, null, 8, ["entries"])
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(WorkloadViewSummaryBlock, {
          icon: "uis uis-clock",
          title: _ctx.$t('workloads.details.summaryTab.time.title')
        }, {
          default: _withCtx(() => [
            _createVNode(WorkloadViewSummaryBlockEntries, { entries: _unref(timesEntries) }, null, 8, ["entries"])
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(WorkloadViewSummaryBlock, {
          icon: "uis uis-desktop",
          title: _ctx.$t('workloads.details.summaryTab.initiator.title')
        }, {
          default: _withCtx(() => [
            _createVNode(WorkloadViewSummaryBlockEntries, { entries: _unref(initiatorEntries) }, null, 8, ["entries"])
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(WorkloadViewSummaryBlock, {
          icon: "exclamation-circle",
          title: _ctx.$t('workloads.details.summaryTab.errors.title')
        }, {
          default: _withCtx(() => [
            _createVNode(WorkloadViewSummaryErrors, _normalizeProps(_guardReactiveProps(_unref(errors))), null, 16)
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(WorkloadViewSummaryBlock, {
          icon: "uis uis-setting",
          title: _ctx.$t('workloads.details.summaryTab.configurations.title')
        }, {
          default: _withCtx(() => [
            _createVNode(WorkloadViewSummaryConfigurations, _normalizeProps(_guardReactiveProps(_unref(configurations))), null, 16)
          ]),
          _: 1
        }, 8, ["title"])
      ])
    ]),
    _: 1
  }))
}
}

})